import { z } from 'zod';
import { AlertEngineStats, DbWatchStats, DispatchStats } from './responses';
import { StorageKind, StorageRun } from './storage';

/**
 * 관제 서버 자신의 상태 계약 (25회차, 조정자 제안 5).
 *
 * 24회차까지 서버가 자기 형편을 말하는 자리는 `/health`(수신 큐·정규화·실시간·프로파일),
 * `/api/storage`(핫·콜드·디스크), `/api/alerts/rules`(규칙·발송), `/api/db/recent`(워처),
 * `/api/external/tokens`(토큰) 넷으로 흩어져 있었다. "지금 관제 서버가 멀쩡한가"를 보려면
 * 네 곳을 따로 열어야 했고, 무엇이 버려지고 있는지는 어느 화면에도 없었다.
 *
 * 여기 하나로 모은다. 값은 전부 **이미 세고 있던 카운터**이고 새로 재는 것은 없다.
 * 저장(규칙 5)도 하지 않는다 — 부를 때마다 메모리와 파일 시스템을 훑은 결과다.
 *
 * `/health`는 그대로 둔다. 그쪽은 compose·nginx가 부르는 기동 확인이고 이쪽은 사람이 보는
 * 화면이다. 헬스체크가 이만큼을 매번 계산하면 그것대로 낭비다.
 */

const count = z.number().int().nonnegative();
const bytes = z.number().int().nonnegative();
const epochMs = z.number().int().nonnegative();

/** 관제 서버 프로세스 자신 */
export const SelfProcess = z.object({
  /** 이 프로세스가 뜬 시각 */
  startedAt: epochMs,
  uptimeMs: count,
  pid: count,
  /** `process.version` (예: v22.23.1) */
  node: z.string(),
  /** `apps/server/package.json`의 version. 못 읽으면 `unknown` */
  version: z.string(),
  /** 빌드할 때 넣은 커밋(`APM_GIT_COMMIT`). 없으면 없다 */
  commit: z.string().optional(),
  rssBytes: bytes,
  heapUsedBytes: bytes,
  heapTotalBytes: bytes,
});
export type SelfProcess = z.infer<typeof SelfProcess>;

/** OTLP 수신 큐 (4회차 결정 2). 버린 수가 0이 아니면 대상 앱이 우리보다 빠르다는 뜻이다 */
export const SelfIngest = z.object({
  queuedBatches: count,
  queuedSpans: count,
  /** 받은 배치 누적 수 (25회차). span 수만 보면 "한 번에 몰려 오는가"를 못 가른다 */
  acceptedBatches: count,
  acceptedSpans: count,
  /** 큐가 차서 버린 수. 0이 아니면 상한(`APM_INGEST_QUEUE_SPANS`)을 올리거나 원인을 봐야 한다 */
  droppedSpans: count,
  droppedBatches: count,
  /**
   * 받지 않는 신호(`/v1/metrics`·`/v1/logs`)를 204로 흘린 수 (29회차).
   * 0이 아니면 어느 앱이 exporter를 못 끈 것이다(`OTEL_METRICS_EXPORTER=none`).
   * 옛 서버 응답에는 없어 기본값을 준다
   */
  ignoredSignals: count.default(0),
  /**
   * 받은 배치를 수신 인코딩별로 센 값 (31회차). 개발 서버에서 프록시 없이
   * "지금 protobuf 로 받고 있는가"를 보는 자리다. `json + protobuf`가 곧 `acceptedBatches`이고
   * `gzip`은 그중 눌려서 온 것이라 겹친다. 옛 서버 응답에는 없어 기본값을 준다
   */
  encodings: z
    .object({ json: count, protobuf: count, gzip: count })
    .default({ json: 0, protobuf: 0, gzip: 0 }),
  /**
   * 수신 검증이 받지 않은 span 누적 수 (49회차, D41-1). 모양이 틀린 id·시각, 받은 시각 ±1일 밖의
   * 시각이 여기 든다. 0이 아니면 보내는 앱의 계측이나 시계를 봐야 한다. 41회차에는 1분 요약
   * 로그와 `/health`로만 보였다. 옛 서버 응답에는 없어 기본값을 준다
   */
  rejectedSpans: count.default(0),
  /** 본문을 읽지 못해 400·413·415로 되돌린 배치 누적 수 (49회차, D41-1) */
  rejectedBatches: count.default(0),
  /**
   * 동시 처리 상한(`APM_INGEST_MAX_CONCURRENT`)에 걸려 503 으로 되돌린 배치 누적 수 (61회차).
   * 보내는 쪽이 다시 보내므로 버린 수가 아니다. 계속 늘면 상한을 올리거나 보내는 쪽 배치 간격을 볼 것.
   * 그 전 서버의 응답에는 없다
   */
  rejectedBusy: count.default(0),
});
export type SelfIngest = z.infer<typeof SelfIngest>;

/** span 트리 → 요약 (4회차 결정 3). 폐기가 늘고 있는지가 이 절의 요점이다 */
export const SelfNormalize = z.object({
  /** 지금 확정을 기다리는 trace 수 */
  pendingTraces: count,
  spans: count,
  completed: count,
  /** 모양이 깨져 버린 span */
  droppedSpans: count,
  /** root가 제 시간에 안 와서 버린 trace */
  droppedNoRoot: count,
  /** 상한을 넘겨 밀려났는데 root가 없어 버린 trace */
  evictedTraces: count,
  overflowCompleted: count,
  droppedInvalidRows: count,
  droppedInvalidSpans: count,
  truncatedSpans: count,
  skippedSpans: count,
  skippedTxs: count,
  /** 다른 서비스의 CLIENT span을 부모로 둔 SERVER span을 그 서비스의 root로 올린 수 (26회차) */
  remoteRoots: count.default(0),
  /** 근거 없이 대기 상한을 넘겨 root로 확정한 수. 호출자가 관제 밖이면 여기로 센다 */
  timeoutRoots: count.default(0),
  /**
   * 부모 없는 CLIENT·PRODUCER root를 워커로 받은 수 (31회차). **정보용이다** — 그 trace는
   * 지금까지처럼 워커 1분 집계로 간다. 0이 아니면 요청 밖의 나가는 호출이 그 서비스 이름으로
   * 오고 있다는 뜻이다. 옛 서버 응답에는 없다
   */
  extRoots: count.default(0),
  /** 지금 확정을 기다리는 span 수. 상한은 `APM_NORMALIZE_MAX_SPANS`다. 그 전 서버의 응답에는 없다 */
  pendingSpans: count.default(0),
  /** trace 하나의 자식 상한(`APM_NORMALIZE_MAX_CHILDREN_PER_TRACE`)에 걸려 버린 span */
  droppedChildren: count.default(0),
  /** 누적기 전체 span 상한에 걸려 밀어낸 trace. 밀려난 뒤 root 가 있으면 확정, 없으면 버린다 */
  spanEvictions: count.default(0),
  /** 누적기 속성 길이 상한(`APM_INGEST_MAX_ATTR_BYTES`)에 걸려 잘라 담은 속성 값 */
  clippedAttributes: count.default(0),
  /** 확정한 trace 에 늦게 온 자식이 든 상태. 자식이 전부 늦은 자식이면 `APM_NORMALIZE_LATE_CHILD_MS`만 기다린다 */
  lateTraces: count.default(0),
  /** root 없이 버린 상태에 있던 늦은 자식 */
  lateDroppedSpans: count.default(0),
  /** 확정 span id 기억이 상한에 차서 늦은 자식을 알아보지 못하게 된 trace */
  lateMemoryFull: count.default(0),
  /**
   * sweep 한 번의 확정 수 상한(`APM_NORMALIZE_MAX_FINISH_PER_SWEEP`)에 닿아 나머지를 다음 sweep 으로 미룬 횟수
   * (61회차). 미룬 trace 는 1초 뒤에 확정된다. 그 전 서버의 응답에는 없다
   */
  sweepDeferred: count.default(0),
});
export type SelfNormalize = z.infer<typeof SelfNormalize>;

/** 워커 1분 집계 (2026-09-07 결정 1) */
export const SelfWorkerAgg = z.object({
  rows: count,
  emitted: count,
  openBuckets: count,
});
export type SelfWorkerAgg = z.infer<typeof SelfWorkerAgg>;

/** 핫 저장기 하나. 종류마다 한 줄이다 */
export const SelfWriter = z.object({
  kind: StorageKind,
  /** 아직 파일로 안 나간 행 */
  bufferedRows: count,
  /** 지금까지 만든 파일 수(회전 횟수) */
  files: count,
  rows: count,
  /** flush 실패 횟수. 0이 아니면 디스크나 권한을 봐야 한다 */
  errors: count,
  /** 줄 선 행이 상한(`APM_STORAGE_MAX_PENDING_ROWS`)을 넘어 버린 행. 그 전 서버의 응답에는 없다 */
  droppedRows: count.default(0),
});
export type SelfWriter = z.infer<typeof SelfWriter>;

/** 콜드 변환 배치 (11회차 결정 1) */
export const SelfCold = z.object({
  runs: count,
  converted: count,
  failed: count,
  rows: count,
  /** 지금 변환이 돌고 있는지 */
  busy: z.boolean(),
  lastRun: StorageRun.optional(),
  /**
   * 합치기·보존이 같은 날짜를 잡고 있어 변환을 건너뛴 누적 수 (49회차, D42-7).
   * 서버는 25회차부터 세고 있었지만 계약에 칸이 없어 화면이 버렸다. 옛 서버 응답에는 없다
   */
  skippedLocked: count.default(0),
});
export type SelfCold = z.infer<typeof SelfCold>;

/** 핫 파일 시간 단위 합치기 (25회차, 조정자 제안 6) */
export const SelfCompact = z.object({
  enabled: z.boolean(),
  runs: count,
  /** 합쳐서 만든 파일 수 */
  compacted: count,
  /** 그 안에 들어간 원본 파일 수 */
  sourceFiles: count,
  rows: count,
  errors: count,
  /** 다른 정리 배치가 잡고 있어 건너뛴 수 (25회차) */
  skippedLocked: count,
  /** 끊긴 뒤 남은 중간 상태를 되돌린 수 (25회차) */
  recovered: count,
  /** 마지막으로 한 바퀴 돌은 시각. 아직 안 돌았으면 없다 */
  lastRunAt: epochMs.optional(),
});
export type SelfCompact = z.infer<typeof SelfCompact>;

/** 보존 정리 한 바퀴의 결과 (25회차). 콜드의 `lastRun`과 같은 자리다 */
export const SelfRetentionRun = z.object({
  startedAt: epochMs,
  endedAt: epochMs,
  /** 이번 바퀴에 지운 폴더·파일 수 */
  removed: count,
  /** 보존 기간이 지났지만 장기 보관본이 없어 남겨 둔 수 */
  keptNoCold: count,
  /** 다른 정리 배치가 그 날짜를 잡고 있어 건너뛴 수 */
  skippedLocked: count,
  errors: count,
});
export type SelfRetentionRun = z.infer<typeof SelfRetentionRun>;

/** 보존 정리 (11회차 결정 3) */
export const SelfRetention = z.object({
  runs: count,
  removedDirs: count,
  /** 보존 기간이 지났지만 장기 보관본이 없어 남겨 둔 날짜 수 */
  keptNoCold: count,
  removedCold: count,
  /** 디스크가 모자라 지운 날짜 수. 0이 아니면 용량을 봐야 한다 */
  removedForSpace: count,
  errors: count,
  /** 다른 정리 배치가 잡고 있어 건너뛴 누적 수 (25회차) */
  skippedLocked: count,
  /** 마지막 한 바퀴. 누적값만으로는 언제 무엇을 지웠는지 알 수 없다 (25회차) */
  lastRun: SelfRetentionRun.optional(),
});
export type SelfRetention = z.infer<typeof SelfRetention>;

/** 조회 엔진의 실행 줄 (15·21회차) */
export const SelfQuery = z.object({
  /** 줄 이름별로 지금 서 있는 실행 수 */
  queued: z.record(z.string(), count),
  /** 줄이 상한까지 차서 거절한 누적 건수 */
  rejected: count,
  /** 시간 상한이 지나 `interrupt()`로 끊은 조회 수 (59회차). 그 전 서버의 응답에는 없다 */
  interrupted: count.default(0),
  /** 줄에서 기다리다 시간 상한이 지나 돌리지 않은 실행 수 (59회차). 그 전 서버의 응답에는 없다 */
  skippedExpired: count.default(0),
});
export type SelfQuery = z.infer<typeof SelfQuery>;

/** 실시간 채널 (5회차 결정 8·9) */
export const SelfRt = z.object({
  received: count,
  /** 모양이 틀려 거절한 스냅샷 수 */
  rejected: count,
  /**
   * 진입점·현재 span 이름이 4,096자를 넘어 잘라 받은 스냅샷 수 (57회차). 긴 URL 요청이 진행 중이었다는 뜻이다.
   * 그 전 서버의 응답에는 없다
   */
  clipped: count.default(0),
  /** 지금 붙어 있는 화면 수 */
  subscribers: count,
  instances: count,
  snapshots: count,
  evictedInstances: count,
  /**
   * 인스턴스 생존 (26회차). `instances`는 링버퍼가 들고 있는 수이고 이쪽은 생존 판정 결과다.
   * 스냅샷 없이 트레이스만 오는 인스턴스는 링버퍼에 없으므로 두 수가 다를 수 있다
   */
  presence: z
    .object({
      instances: count,
      /** 신호가 끊겨 조용해진 인스턴스 수 */
      stale: count,
      /** 스냅샷 없이 트레이스만 오는 인스턴스 수 */
      traceOnly: count,
      /** 같은 이름에 다른 프로세스가 뜬 것을 본 횟수 */
      restarts: count,
      /**
       * 같은 이름을 여러 프로세스가 쓰고 있는 것을 **본 횟수**(서버 기동 뒤 누적, 줄지 않는다).
       * 지금 몇 개가 그런지가 아니다. 지금 상태는 인스턴스 줄의 `conflict`로 본다
       */
      conflicts: count,
      /** 신호가 끊겨 목록에서 뺀 수 */
      gone: count,
      /**
       * 고유 키 상한(`APM_RT_MAX_PRESENCE`)을 넘겨 버린 수 (리뷰 R26-09).
       * 0이 아니면 계측이 인스턴스 이름을 요청마다 새로 만들고 있을 수 있다
       */
      evicted: count.default(0),
    })
    .default({
      instances: 0,
      stale: 0,
      traceOnly: 0,
      restarts: 0,
      conflicts: 0,
      gone: 0,
      evicted: 0,
    }),
  /**
   * 트레이스만 서비스의 초당 완료·오류·p95 집계 (34회차). 끝난 요청을 1분 구간으로 접어
   * 스냅샷 링버퍼와 같은 창만큼 들고 있는 것이다. **트레이스만 서비스 것만** 들고, 크기는
   * 고정 길이 링이라 입력에 따라 늘지 않는다. 34회차 전 서버의 응답에는 없다
   */
  traffic: z
    .object({
      /** 지금 들고 있는 1분 구간 수 */
      buckets: count,
      /** 구간을 통틀어 들고 있는 키(서비스+인스턴스) 수 */
      slots: count,
      /**
       * 한 구간의 키 상한에 걸려 못 센 행 수. 0이 아니면 계측이 인스턴스 이름을
       * 요청마다 새로 만들고 있을 수 있다
       */
      droppedKeys: count,
      /**
       * 끝난 시각이 지금보다 너무 앞서 버린 행 수. 0이 아니면 그 앱의 시계가 어긋나 있다.
       * 34회차 첫 판에는 없어 기본값을 둔다
       */
      droppedFuture: count.default(0),
      /**
       * 실시간 인스턴스가 있는 서비스라 접지 않고 넘긴 행 수. 이 집계는 트레이스만 서비스
       * 것만 든다. 34회차 첫 판에는 없어 기본값을 둔다
       */
      skippedRealtime: count.default(0),
    })
    .default({ buckets: 0, slots: 0, droppedKeys: 0, droppedFuture: 0, skippedRealtime: 0 }),
  /** 스냅샷 링버퍼가 지금 들고 있는 추정 바이트(JSON 길이 합). 그 전 서버의 응답에는 없다 */
  bytes: bytes.default(0),
  /** 인스턴스별 바이트 예산(`APM_RT_RING_MAX_BYTES_PER_INSTANCE`)에 걸려 버린 스냅샷의 추정 바이트 누적 */
  evictedBytes: bytes.default(0),
  /** 상세 화면용 최근 span 버퍼가 들고 있는 추정 바이트. 그 전 서버의 응답에는 없다 */
  spanFeedBytes: bytes.default(0),
  /** 그 버퍼가 바이트 상한(`APM_SPAN_FEED_MAX_BYTES`)에 걸려 버린 추정 바이트 누적 */
  spanFeedEvictedBytes: bytes.default(0),
});
export type SelfRt = z.infer<typeof SelfRt>;

/** CPU 프로파일 수신 (8회차 결정 2) */
export const SelfProfile = z.object({
  received: count,
  rejected: count,
  stored: count,
  errors: count,
  /** 같은 서비스·날짜·id가 이미 있어 409로 거절한 수 (57회차). 그 전 서버의 응답에는 없다 */
  duplicates: count.default(0),
  /** 서비스별 시간당 상한에 걸려 429로 거절한 수 (57회차) */
  throttled: count.default(0),
  /** 디스크 여유가 모자라 507로 거절한 수 (57회차) */
  diskFull: count.default(0),
});
export type SelfProfile = z.infer<typeof SelfProfile>;

/** 알림 엔진·발송 (10·16·24회차) */
export const SelfAlert = z.object({
  engine: AlertEngineStats,
  dispatch: DispatchStats,
  /** 규칙 파일 경로. 비어 있으면 알림을 켜지 않았다 */
  rulesFile: z.string(),
  /** 모양이 틀려 버린 규칙의 이유. 한글 한 줄씩 */
  problems: z.array(z.string()),
  /**
   * 창 집계 규칙 전체의 행 예산(`APM_ALERT_WINDOW_MAX_ROWS_TOTAL`)에 걸려 버린 끝난 요청 행 누적 수 (61회차).
   * 예산을 넘으면 **가장 큰 창부터** 가장 오래된 행을 깎는다 — 큰 창들이 같은 높이로 줄고, 상한이 창 수보다
   * 작지 않은 한 어떤 규칙의 창도 비지 않는다. 0이 아니면 깎인 규칙의 값은 창 전체가 아니라 남은 최근 행으로
   * 재어지고 있다. 어느 규칙인지는 `windowTruncatedByRule`. 그 전 서버의 응답에는 없다
   */
  windowTruncated: count.default(0),
  /** 규칙 id → 그 규칙의 창에서 예산 때문에 버린 행 누적 수. 버린 적이 있는 규칙만 있다. 그 전 서버의 응답에는 없다 */
  windowTruncatedByRule: z.record(z.string(), count).default({}),
});
export type SelfAlert = z.infer<typeof SelfAlert>;

/** 외부 연동 토큰 (16·23-B회차) */
export const SelfExternal = z.object({
  /** 발급돼 살아 있는 토큰 수 */
  activeTokens: count,
  /** `.env`의 부트스트랩 토큰이 있는지 */
  envToken: z.boolean(),
  /** 저장 파일이 깨져 발급·폐기가 막혔는지 */
  blocked: z.boolean(),
});
export type SelfExternal = z.infer<typeof SelfExternal>;

/** 서버가 자기 로그에서 들고 있는 한 줄 (25회차). 경고 이상만 남긴다 */
export const SelfLogLine = z.object({
  at: epochMs,
  level: z.enum(['warn', 'error']),
  /** `[ingest]`처럼 로그 앞에 붙는 이름 */
  context: z.string().optional(),
  /** 한글 문장 그대로. 잘리면 뒤에 `…`가 붙는다 */
  message: z.string(),
});
export type SelfLogLine = z.infer<typeof SelfLogLine>;

/** `GET /api/self` 응답 */
export const SelfStatus = z.object({
  at: epochMs,
  process: SelfProcess,
  /** 저장 루트. `/api/storage`가 주는 값과 같다 */
  dataDir: z.string(),
  diskFreeMb: z.number().nonnegative().optional(),
  diskTotalMb: z.number().nonnegative().optional(),
  /** 디스크 여유가 이 밑이면 오래된 장기 보관본부터 지운다 */
  diskMinFreeMb: count,
  ingest: SelfIngest,
  normalize: SelfNormalize,
  workerAgg: SelfWorkerAgg,
  writers: z.array(SelfWriter),
  cold: SelfCold,
  compact: SelfCompact,
  retention: SelfRetention,
  query: SelfQuery,
  rt: SelfRt,
  profile: SelfProfile,
  dbWatch: DbWatchStats,
  alert: SelfAlert,
  external: SelfExternal,
  /** 최근 경고·오류. 새 것이 앞이다 (`APM_SELF_LOG_KEEP`) */
  logs: z.array(SelfLogLine),
});
export type SelfStatus = z.infer<typeof SelfStatus>;
